import { resolveFeatureFlags } from "@arc/tenant-config";
import { PlatformSelector } from "./PlatformSelector.js";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import type { ReactNode } from "react";
import { useQrSvg } from "./qr/use-qr-svg.js";
import { Bookmark, Eye, Link2, Loader2, Pencil, Printer, QrCode, Star, X, type LucideIcon } from "lucide-react";
import type { Manual, ManualAssignedUser, ManualLinkCard, ManualLinks } from "@arc/contracts";
import type { AuthSession } from "../../auth/keycloak.js";
import { Drawer, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { usePrintManual } from "./print/usePrintManual.js";
import { ManualPrintSheet } from "./print/ManualPrintSheet.js";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { CategoryIcon } from "@/components/icons";
import { cn } from "@/lib/utils";
import { formatDateTime } from "@/lib/date";
import { initials } from "@/lib/initials";
import { ManualReferenceElement } from "./ManualReferenceElement.js";
import { ManualStateBadge } from "./ManualStateBadge.js";
import { ManualHandspeedSection } from "./ManualHandspeedSection.js";
import { ManualKebabMenu } from "./ManualKebabMenu.js";
import { ManualStatusStepper } from "./ManualStatusStepper.js";
import { availableManualTransitions, type ManualTransitionKey } from "./manual-transitions.js";

export interface ManualDetailsDrawerProps {
  manual: Manual;
  /** Archived context (from the grid's Archived filter): hides the favourite star + switches the kebab to
   *  Unarchive/Delete — mirrors {@link ManualCard} (ADR-016 panel #3; the `Manual` contract has no `archived` field). */
  archived: boolean;
  /** Best-effort gate for the kebab actions (server-authoritative). */
  canManage: boolean;
  /** The caller has the facility `confirm` tier — reveals the publish/unpublish/clear-flag transitions (panel). */
  canConfirm: boolean;
  /** The caller's LOCAL user id (`/me.id`, ADR-002 Phase A) — the View/Edit gate's assignment check. `null` while `/me`
   *  loads (Edit stays disabled — never a false-enable). */
  myId: number | null;
  /** Open the authoring surface for an editable manual (ADR-002 Phase 10). Until wired, an edit-entitled caller sees a
   *  disabled "Coming soon" Edit; a view-only caller always sees Edit disabled with an honest reason. */
  onEdit?: (m: Manual) => void;
  /** Open the runtime viewer for this manual (ADR-002 Phase 5). Visible ⇒ viewable, so View is enabled whenever wired. */
  onView?: (m: Manual) => void;
  /** The reader / editor are URL routes in THIS realm (`?arc_manual=` / `?arc_edit=`) — the drill-down navigation.
   *  Classic realms render the reader locally and ignore those params, so a copied link would land on facility
   *  selection: the share links + QR are offered only here (diff-panel batch Medium #2). */
  shareable?: boolean;
  isFavorite: boolean;
  onToggleFavorite: (id: number) => void;
  onDuplicate: (m: Manual) => void;
  onMove: (m: Manual) => void;
  onArchive: (m: Manual) => void;
  onUnarchive: (m: Manual) => void;
  onDelete: (m: Manual) => void;
  /** Fire a review transition (ADR-018) — the view maps the key to a mutation (or the mark dialog) and refreshes THIS
   *  drawer in place. **Optional since the ADR-018 addendum §3 sweep**: where the editor is reachable it owns the
   *  review workflow and this is omitted, leaving the status track read-only. Classic-nav realms have no editor, so
   *  they still pass it — see {@link FacilityManualsView}. */
  onTransition?: (m: Manual, key: ManualTransitionKey) => void;
  /** The manual's assigned reviewers (ADR-019) — fetched by the caller (drawer stays presentational). `undefined` while
   *  loading; `assignedUsersError` distinguishes a load failure from a genuinely-empty list. */
  assignedUsers: ManualAssignedUser[] | undefined;
  assignedUsersLoading: boolean;
  assignedUsersError: boolean;
  /** Open the Manage-reviewers dialog (ADR-022) — the section's "Manage" affordance, shown only to a facility-`confirm`
   *  caller (`canConfirm`) when provided. Omitted where interactive assign isn't wired (e.g. the classic realms). */
  onManageReviewers?: (m: Manual) => void;
  /** The manual's rating summary (ADR-021) — fetched by the caller; the header shows the average. `average` is null when
   *  there are no ratings; `ratingsLoading` hides the badge until loaded (no flash); `ratingsError` shows a distinct
   *  "unavailable" badge (NOT "Not rated") so a load failure isn't misreported as a genuinely-unrated manual. */
  ratingAverage: number | null;
  ratingCount: number;
  ratingsLoading: boolean;
  ratingsError: boolean;
  /** Linked manuals (ADR-020) — the `parent` (if this manual is a linked child) + the `children` (if it's a parent),
   *  fetched by the caller. `undefined` while loading. Read-only display for now (cross-facility navigation is a
   *  follow-up slice — the card shows each target's facility + archived state so the location is clear). */
  links: ManualLinks | undefined;
  linksLoading: boolean;
  linksError: boolean;
  /** Navigate to a linked manual (its facility's manuals view + open its drawer) — the round primary arrow on each link
   *  card (ADR-020). When omitted (e.g. the classic realms with no URL nav) the arrow is not rendered. */
  onNavigateManual?: (card: ManualLinkCard) => void;
  /** Create a linked copy of THIS manual (ADR-020 addendum) — the "Create link" action on the Linked-manuals section,
   *  shown to a `manuals_manage` caller on a PUBLISHED source that isn't itself a link (its links must be loaded first).
   *  Opens the placement drawer (create-and-place). Omitted where linking isn't wired. */
  onCreateLink?: (m: Manual) => void;
  /** Remove a linked child (ADR-020 addendum) — the × on each child card, shown to a `manuals_manage` caller.
   *  `unlinkingId` is the child currently being removed (its × spins). */
  onUnlink?: (card: ManualLinkCard) => void;
  unlinkingId?: number | null;
  /** Inline copy for a failed create-link / unlink (shown in the Linked-manuals section). */
  linkActionError?: string | null;
  /** Needed only by the hand-speed section, which owns its own query (ADR-039) rather than adding five more props. */
  session: AuthSession | null;
  /** Open the hand-speed device picker, layered OVER this drawer (the Manage-reviewers pattern). Omitted where the
   *  rig isn't wired. */
  onChangeHandspeed?: (m: Manual) => void;
  /** The rig's flag changed here — the view patches its snapshot so the "Handspeed" type badge and the grid card stay
   *  truthful without a reopen. */
  onHandspeedChange?: (id: number, enabled: boolean) => void;
  onClose: () => void;
}

/**
 * The per-manual details slide-over (ADR-016 §D) — the full legacy "Handlungsanweisung" panel: identity (category +
 * lifecycle badge + **version badge** + **average-rating badge**), title, description, a READ-ONLY release-status
 * indicator, and legacy-style **Created / Last-edited** blocks (ADR-015 — {t("manualManagement.details2.date")} + User on separate lines, with the time).
 * The favourite **bookmark** (a bookmark, NOT a star — the star means the rating; legacy also bookmarks the title) + the
 * lifecycle kebab branch on `archived` exactly like the card. Everything that needs a not-yet-built module — **View /
 * Edit** (viewer/authoring — **Edit** is permission-gated per ADR-002 Phase A: control OR manuals_manage+assigned),
 * **Share** (links / print / QR), **Assigned users**, **Links** — is a visibly-disabled "Coming soon" affordance. No per-manual Activity timeline (legacy has none in this panel; the `/history` API stays for
 * the future global feed). Modal (the shared {@link Drawer}): focus-trapped, Esc/backdrop dismiss, restores focus.
 */
export function ManualDetailsDrawer({
  manual,
  archived,
  canManage,
  canConfirm,
  myId,
  onEdit,
  onView,
  isFavorite,
  onToggleFavorite,
  onDuplicate,
  onMove,
  onArchive,
  onUnarchive,
  onDelete,
  onTransition,
  assignedUsers,
  assignedUsersLoading,
  assignedUsersError,
  onManageReviewers,
  ratingAverage,
  ratingCount,
  ratingsLoading,
  ratingsError,
  links,
  linksLoading,
  linksError,
  onNavigateManual,
  onCreateLink,
  onUnlink,
  unlinkingId,
  linkActionError,
  session,
  onChangeHandspeed,
  onHandspeedChange,
  onClose,
  shareable = false,
}: ManualDetailsDrawerProps) {
  const { t } = useTranslation();
  // Print (ADR-031): the legacy share-section gate — a PUBLISHED (confirmed / marked), not-archived manual. Linked
  // manuals print too (their body read returns the parent's content). Works in every realm: no route involved.
  const print = usePrintManual();
  const canPrint = (manual.state === "confirmed" || manual.state === "marked") && !archived;
  const printControl = canPrint ? (
    <Button variant="outline" size="sm" onPress={() => print.start(manual.id)} isDisabled={print.printing !== null} className="dark:border-border gap-1.5">
      {print.printing !== null ? <Loader2 aria-hidden="true" className="size-3.5 animate-spin" /> : <Printer aria-hidden="true" className="size-3.5" />}
      {print.printing !== null ? t("manualManagement.misc.preparingPrint") : t("manualManagement.misc.print")}
    </Button>
  ) : (
    <ComingSoon icon={Printer} label={t("manualManagement.misc.print")} variant="outline" />
  );
  // Rich-text description, server-sanitized at EVERY read boundary (the list projection and the manual DTO, ADR-013 §3)
  // — rendered formatted like the viewer's start page, not flattened to text (PARITY-GAPS §1, 2026-09-10).
  const description = manual.description;
  // The review actions for the current state (ADR-018) — shown as buttons under the status track, filtered to what the
  // caller MAY do (tier-gated: publish/unpublish steps need `confirm`). Empty for an archived manual (out of the workflow).
  // Only where this drawer OWNS the workflow (no reachable editor). Otherwise the track is display-only and review
  // status changes solely in the editor's status control, so the Check Manual gate cannot be walked past.
  const transitions = archived || !onTransition ? [] : availableManualTransitions(manual.state, { canManage, canConfirm });
  // View/Edit gate (ADR-002 Phase A) — the legacy `canUserSeeManual` EDIT rule: control (facility `confirm`) OR
  // manuals_manage AND an assignee of THIS manual. Purely a UX gate; the authoring API re-checks server-side and also
  // enforces the ADR-018 draft-only invariant. A `null` myId / still-loading assignees ⇒ assignment can't be confirmed
  // ⇒ Edit stays disabled (never a false-enable). The reason is only surfaced when Edit is NOT editable (so !canConfirm).
  const canEdit = canConfirm || (canManage && myId != null && (assignedUsers?.some((u) => u.userId === myId) ?? false));
  const editDisabledReason = !canManage
    ? t("manualManagement.misc.youHaveViewOnlyAccessTo")
    : assignedUsers === undefined
      ? t("manualManagement.misc.checkingYourAccess")
      : t("manualManagement.misc.onlyAssignedReviewersOrAConfirmer");
  const types = [
    manual.isArManual ? t("manualManagement.badge.ar") : null,
    manual.isSensorManual ? t("manualManagement.list.typeSensor") : null,
    manual.handspeedCheck ? t("manualManagement.list.typeHandspeed") : null,
  ].filter((t): t is string => t !== null);
  return (
    <Drawer isOpen isDismissable onOpenChange={(open) => !open && onClose()}>
      <DrawerHeader>
        <div className="min-w-0">
          {/* Eyebrow: category + lifecycle badge + version badge + average-rating badge (owner: version/rating on top). */}
          <div className="mb-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-muted-foreground inline-flex min-w-0 items-center gap-1 text-xs">
              <CategoryIcon aria-hidden="true" className="size-3.5 shrink-0" />
              <span className="truncate">{manual.categoryName}</span>
            </span>
            <ManualStateBadge state={manual.state} comment={manual.markedComment} />
            {/* Version — the "v" prefix is self-describing, so no label (owner). A muted pill. */}
            <span className="bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-xs font-medium tabular-nums">
              v{manual.version}
            </span>
            <RatingBadge average={ratingAverage} count={ratingCount} loading={ratingsLoading} error={ratingsError} />
          </div>
          <DrawerTitle className="text-base">{manual.name}</DrawerTitle>
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          {!archived ? (
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={t(isFavorite ? "manualManagement.card.unfavourite" : "manualManagement.card.favourite", { name: manual.name })}
              aria-pressed={isFavorite}
              onPress={() => onToggleFavorite(manual.id)}
            >
              <Bookmark className={cn("size-4", isFavorite ? "fill-attention text-attention" : "text-muted-foreground")} />
            </Button>
          ) : null}
          <ManualKebabMenu
            manual={manual}
            state={manual.state}
            archived={archived}
            canManage={canManage}
            canConfirm={canConfirm}
            onDuplicate={() => onDuplicate(manual)}
            onMove={() => onMove(manual)}
            onArchive={() => onArchive(manual)}
            onUnarchive={() => onUnarchive(manual)}
            onDelete={() => onDelete(manual)}
            onTransition={onTransition ? (key) => onTransition(manual, key) : undefined}
          />
          <Button variant="ghost" size="icon-sm" aria-label={t("manualManagement.misc.close")} onPress={onClose}>
            <X className="size-4" />
          </Button>
        </div>
      </DrawerHeader>

      {/* Areas separated by hairlines (mockup): each direct child gets a top border + vertical padding; ends flush. */}
      {/* `-mr-6 pr-6 -mb-6 pb-6` pulls the scroll box out through the Drawer's p-6 so the scrollbar runs along the
          window edge and to the bottom (owner request) — the padding moves inside, so the content doesn't shift. */}
      <div className="divide-border -mr-6 -mb-6 flex min-h-0 flex-1 flex-col divide-y overflow-y-auto scroll-thin pr-6 pb-6 [&>*]:py-5 [&>*:first-child]:pt-0 [&>*:last-child]:pb-0">
        {/* Type tags + description — ABOVE the primary actions (mockup polish). */}
        <div className="flex flex-col gap-3">
          {types.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5">
              {types.map((t) => (
                <span
                  key={t}
                  className="bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-[0.65rem] font-medium"
                >
                  {t}
                </span>
              ))}
            </div>
          ) : null}
          {/* Description (plain-text — migrated legacy HTML isn't client-sanitized; formatted read-only comes later). */}
          <section aria-label={t("manualManagement.misc.description")}>
            {description ? (
              <div
                data-testid="manual-description"
                className="text-sm leading-relaxed [&_a]:underline [&_ol]:mb-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:mb-2 [&_ul]:list-disc [&_ul]:pl-5"
                dangerouslySetInnerHTML={{ __html: description }}
              />
            ) : (
              <p className="text-muted-foreground text-sm">{t("manualManagement.details2.noDescription")}</p>
            )}
          </section>
        </div>

        {/* View / Edit — the primary actions, deferred until the viewer/authoring modules ship. Edit is outlined (a
            visible border via ComingSoon's outline handling). */}
        {/* Two equal columns, not flex-1: View and Edit are ALWAYS the same width, whatever the label lengths. */}
        <div className="grid grid-cols-2 gap-2">
          <Button variant="accent" size="lg" className="w-full" onPress={() => onView?.(manual)} isDisabled={!onView}>
            <Eye aria-hidden="true" />
            {t("manualManagement.misc.view")}
          </Button>
          <EditButton canEdit={canEdit} disabledReason={editDisabledReason} onEdit={onEdit ? () => onEdit(manual) : undefined} />
        </div>

        {/* Where it is usable (ADR-057): shown only, set in the editor. Not on a linked copy, which can't set its own. */}
        {manual.parentManualId === null && resolveFeatureFlags(session?.realm ?? "").sidebarPlatformsVisible ? (
          <Section title={t("manualManagement.platforms.title")}>
            <PlatformSelector value={manual} />
          </Section>
        ) : null}

        {/* Release status — the read-only track, plus the review actions ONLY where this surface owns the workflow
            (ADR-018 addendum §3: with an editor reachable, status changes live there behind the Check Manual gate),
            gated by `manuals_manage` (best-effort; the server re-checks, incl. the `confirm` tier for publish steps).
            Firing a transition refreshes this drawer in place, so the newly-available actions appear without reopening. */}
        <Section title={t("manualManagement.misc.releaseStatus")}>
          <ManualStatusStepper state={manual.state} comment={manual.markedComment} />
          {transitions.length > 0 ? (
            <div className="flex flex-wrap gap-2 pt-1">
              {transitions.map((tr) => {
                const Icon = tr.icon;
                return (
                  <Button
                    key={tr.key}
                    variant={tr.destructive ? "destructive" : tr.key === "return" ? "outline" : "default"}
                    size="sm"
                    onPress={() => onTransition?.(manual, tr.key)}
                  >
                    <Icon aria-hidden="true" />
                    {t(tr.labelKey)}
                  </Button>
                );
              })}
            </div>
          ) : null}
        </Section>

        {/* Audit facts (ADR-015 Layer A) — legacy-style labelled blocks (owner): {t("manualManagement.details2.date")} + User on SEPARATE lines so a long
            email never truncates, and the time is included. A person is shown only when known. A hairline separates the
            two blocks (owner). */}
        <div className="divide-border flex flex-col divide-y [&>*]:py-4 [&>*:first-child]:pt-0 [&>*:last-child]:pb-0">
          <AuditBlock title={t("manualManagement.misc.created")} date={manual.createdAt} who={manual.creatorUsername} />
          <AuditBlock title={t("manualManagement.misc.lastEdited")} date={manual.updatedAt} who={manual.lastUpdatedByUsername} />
        </div>

        {/* Assigned reviewers (ADR-019 read + ADR-022 manage). A facility-`confirm` caller gets a "Manage" affordance
            that opens the assign/unassign dialog; everyone else sees the read-only list. */}
        <Section
          title={t("manualManagement.misc.assignedUsers")}
          action={
            canConfirm && onManageReviewers ? (
              <Button variant="outline" size="sm" onPress={() => onManageReviewers(manual)}>
                <Pencil aria-hidden="true" className="size-3.5" />
                {t("manualManagement.misc.manage")}
              </Button>
            ) : undefined
          }
        >
          {assignedUsersLoading ? (
            <p className="text-muted-foreground text-sm">{t("common.loading")}</p>
          ) : assignedUsersError ? (
            <p className="text-muted-foreground text-sm">{t("manualManagement.details.loadReviewersFailed")}</p>
          ) : !assignedUsers || assignedUsers.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t("manualManagement.details2.noReviewers")}</p>
          ) : (
            <ul className="divide-border flex flex-col divide-y">
              {assignedUsers.map((u) => (
                <li key={u.userId} className="flex items-center gap-2 py-2 text-sm first:pt-0 last:pb-0">
                  <span
                    aria-hidden="true"
                    className="bg-muted text-muted-foreground flex size-6 shrink-0 items-center justify-center rounded-full text-[0.65rem] font-medium"
                  >
                    {assigneeInitials(u)}
                  </span>
                  <span className="truncate">{u.name}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* The hand-speed rig (ADR-039). Only a SENSOR manual can have one, exactly as the legacy gates its switch
            (D6), and only someone who may edit the manual sees it — the read is edit-authz'd server-side (D2).
            ARCHIVED and LINKED are part of that gate, not an afterthought: `assertManualEditable` refuses both
            (`MANUAL_NOT_EDITABLE` / `MANUAL_IS_LINK`), so rendering the block there put a permanent "couldn't load"
            banner on every open of a linked sensor manual — reproduced on takeda 2000020498 (diff panel). A link is
            edited through its source, and an archived manual is out of the workflow, exactly like Edit and Share. */}
        {manual.isSensorManual && onChangeHandspeed && !archived && manual.parentManualId === null ? (
          <ManualHandspeedSection
            manualId={manual.id}
            session={session}
            canEdit={canEdit}
            onOpenPicker={() => onChangeHandspeed(manual)}
            onEnabledChange={(enabled) => onHandspeedChange?.(manual.id, enabled)}
          />
        ) : null}

        {/* Share (legacy sidebar `shareContent`): the VIEW link (`?arc_manual=<id>`, copied to the clipboard), the EDIT
            link (`?arc_edit=<id>`, only for someone who may edit), and a QR code of the view link. Print (the legacy
            builds a printable page of the whole manual) is its own slice. */}
        {/* An ARCHIVED manual has no reader and no editor to link to — its share links stay deferred (found on the
            archived-target live check of the arc_details slice). */}
        {print.body ? <ManualPrintSheet body={print.body} onReady={print.onReady} onAfterPrint={print.onAfterPrint} /> : null}
        {print.error ? (
          <p role="alert" className="text-destructive text-sm">
            {print.error}
          </p>
        ) : null}
        {shareable && !archived ? (
          /* The Edit link follows the drawer's OWN edit entitlement (its Edit button's gate), not the mere presence of a
             navigation callback, which every drill-down user has (diff-panel batch Medium #3). */
          <ShareSection manualId={manual.id} canEdit={canEdit && onEdit != null} print={printControl} />
        ) : (
          <Section title={t("manualManagement.misc.share")}>
            <div className="grid grid-cols-2 gap-2">
              <ComingSoon icon={Link2} label={t("manualManagement.misc.viewLink")} variant="outline" />
              <ComingSoon icon={Link2} label={t("manualManagement.misc.editLink")} variant="outline" />
              {printControl}
              <ComingSoon icon={QrCode} label={t("manualManagement.misc.qrCode")} variant="outline" />
            </div>
          </Section>
        )}

        {/* Linked manuals (ADR-020) — the PARENT (if this is a linked child) and/or the CHILDREN (if it's a parent).
            A `manuals_manage` caller on a PUBLISHED source (not itself a link) gets "Create link" (→ placement drawer);
            each child card gets an × to unlink. `published` mirrors the create gate (source must be confirmed/marked). */}
        {(() => {
          const published = manual.state === "confirmed" || manual.state === "marked";
          // Is-a-link comes from the AUTHORITATIVE `manual.parentManualId` (on the DTO), NOT `links.parent` — a child
          // whose parent is in a facility the caller can't view has `links.parent === null` (view-filtered) yet is still
          // a link, and would 409 on create (panel R2). A link can't be linked again.
          const canLink = !!(canManage && onCreateLink && !archived && published && manual.parentManualId === null);
          return (
            <Section
              title={t("manualManagement.misc.linkedManuals")}
              action={
                canLink ? (
                  <Button variant="outline" size="sm" onPress={() => onCreateLink(manual)}>
                    <Link2 aria-hidden="true" className="size-3.5" /> {t("manualManagement.misc.createLink")}
                  </Button>
                ) : undefined
              }
            >
              {linkActionError ? (
                <p role="alert" className="text-destructive text-sm">
                  {linkActionError}
                </p>
              ) : null}
              {linksLoading ? (
                <p className="text-muted-foreground text-sm">{t("common.loading")}</p>
              ) : linksError ? (
                <p className="text-muted-foreground text-sm">{t("manualManagement.details2.loadLinkedFailed")}</p>
              ) : (links?.parent ?? null) === null && (links?.children.length ?? 0) === 0 ? (
                <p className="text-muted-foreground text-sm">{t("manualManagement.details2.noLinked")}</p>
              ) : (
                <div className="flex flex-col gap-4">
                  {links?.parent ? (
                    <div className="flex flex-col gap-1.5">
                      <p className="text-muted-foreground text-xs">{t("manualManagement.details2.linkedFrom")}</p>
                      <LinkCard card={links.parent} onNavigate={onNavigateManual} />
                    </div>
                  ) : null}
                  {links && links.children.length > 0 ? (
                    <div className="flex flex-col gap-1.5">
                      <p className="text-muted-foreground text-xs">
                        {t("manualManagement.details2.linkedCountFull", { count: links.children.length })}
                      </p>
                      <ul className="flex flex-col gap-2">
                        {links.children.map((c) => (
                          <li key={c.id}>
                            <LinkCard
                              card={c}
                              onNavigate={onNavigateManual}
                              // Per-card: the × is gated on the caller's manage on the CHILD's OWN facility (panel #3),
                              // not the source facility's `canManage` — a child lives elsewhere.
                              onRemove={c.canManage && onUnlink ? () => onUnlink(c) : undefined}
                              removing={unlinkingId === c.id}
                            />
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              )}
            </Section>
          );
        })()}
      </div>
    </Drawer>
  );
}

function Section({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section aria-label={title} className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}


/** Reviewer avatar initials — prefer first-name + last-name initials (when the token carried them), else fall back to the
 *  username/email (`name`). Codepoint-aware (astral-safe). */
function assigneeInitials(u: ManualAssignedUser): string {
  if (u.firstName && u.lastName) {
    return ((Array.from(u.firstName)[0] ?? "") + (Array.from(u.lastName)[0] ?? "")).toUpperCase();
  }
  return initials(u.name);
}

/** A legacy-style audit block ("Created" / "Last edited"): a titled section with Date and User on SEPARATE rows, so a
 *  long email fills its own row (no truncation — owner request). The user row is omitted when the person is unknown. */
function AuditBlock({ title, date, who }: { title: string; date: string; who: string | null }) {
  const { t } = useTranslation();
  return (
    <section aria-label={title} className="flex flex-col gap-1">
      <h3 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">{title}</h3>
      <dl className="grid grid-cols-[auto_1fr] items-baseline gap-x-4 gap-y-0.5 text-sm">
        <dt className="text-muted-foreground">{t("manualManagement.details2.date")}</dt>
        <dd className="text-right tabular-nums">{formatDateTime(date)}</dd>
        {who ? (
          <>
            <dt className="text-muted-foreground">{t("manualManagement.details2.user")}</dt>
            <dd className="text-right break-all">{who}</dd>
          </>
        ) : null}
      </dl>
    </section>
  );
}

/** The manual's average rating in the header (ADR-021): a ★ + the average (1 dp) + the count. Filled star when rated, a
 *  muted "Not rated" when there are none, a distinct "—" (aria "Rating unavailable") on a load ERROR so a failure isn't
 *  misreported as unrated (diff-panel), hidden while loading (no flash). Deliberately a STAR — distinct from the favourite
 *  BOOKMARK — so the two never read as the same control. */
function RatingBadge({
  average,
  count,
  loading,
  error,
}: {
  average: number | null;
  count: number;
  loading: boolean;
  error: boolean;
}) {
  const { t } = useTranslation();
  if (loading) return null;
  if (error) {
    return (
      <span className="text-muted-foreground inline-flex items-center gap-1 text-xs" aria-label={t("manualManagement.misc.ratingUnavailable")}>
        <Star aria-hidden="true" className="size-3" />—
      </span>
    );
  }
  const rated = count > 0 && average !== null;
  return (
    <span
      className="text-muted-foreground inline-flex items-center gap-1 text-xs tabular-nums"
      aria-label={
        rated ? t("manualManagement.details2.averageRating", { average: average.toFixed(1), count }) : t("manualManagement.misc.notRatedYet")
      }
    >
      <Star aria-hidden="true" className={cn("size-3", rated && "fill-attention text-attention")} />
      {rated ? (
        <span>
          {average.toFixed(1)}
          <span className="opacity-70"> · {count}</span>
        </span>
      ) : (
        t("manualManagement.misc.notRated")
      )}
    </span>
  );
}

/** A linked manual (ADR-020) as a manual-reference-element, with an Archived tag and, for a manuals_manage caller, unlink.
 *  The facility it shows matters here: a linked child lives under a DIFFERENT facility. "Öffnen" jumps to that manual —
 *  its facility's manuals view + its drawer. */
function LinkCard({
  card,
  onNavigate,
  onRemove,
  removing,
}: {
  card: ManualLinkCard;
  onNavigate?: (card: ManualLinkCard) => void;
  /** Unlink (remove) this linked child (ADR-020) — a ghost × before the navigate arrow, for a manuals_manage caller. */
  onRemove?: () => void;
  removing?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <ManualReferenceElement
      manual={card}
      path={card.facilityName}
      onOpen={onNavigate ? () => onNavigate(card) : undefined}
      badge={
        card.archived ? (
          <span className="bg-muted text-muted-foreground shrink-0 rounded-full px-2 py-0.5 text-[0.7rem] font-medium">{t("common.archived")}</span>
        ) : null
      }
      actions={
        onRemove ? (
          <TooltipTrigger delay={300}>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={t("manualManagement.details2.unlink", { name: card.name })}
              onPress={onRemove}
              isDisabled={removing}
              className="text-muted-foreground hover:text-destructive shrink-0"
            >
              {removing ? <Loader2 aria-hidden="true" className="size-4 animate-spin" /> : <X aria-hidden="true" className="size-4" />}
            </Button>
            <Tooltip>{t("manualManagement.details2.removeLink")}</Tooltip>
          </TooltipTrigger>
        ) : null
      }
    />
  );
}

/** A deferred action: a disabled button carrying a "Coming soon" tooltip (kept in the layout, honestly non-functional). */
/** The share links + QR of the legacy sidebar. Links are THIS app's routes (the reader / the editor), so a copied link
 *  opens the rewrite. The clipboard is the only side effect; "Copied" confirms it for a moment. */
function ShareSection({ manualId, canEdit, print }: { manualId: number; canEdit: boolean; print: React.ReactNode }) {
  const { t } = useTranslation();
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const viewLink = `${origin}/?arc_manual=${String(manualId)}`;
  const editLink = `${origin}/?arc_edit=${String(manualId)}`;
  const [copied, setCopied] = useState<"view" | "edit" | null>(null);
  const [showQr, setShowQr] = useState(false);
  const svg = useQrSvg(showQr ? viewLink : "");
  const copy = (which: "view" | "edit", text: string) => {
    void navigator.clipboard?.writeText(text).then(
      () => {
        setCopied(which);
        window.setTimeout(() => setCopied((c) => (c === which ? null : c)), 1500);
      },
      () => undefined,
    );
  };
  return (
    <Section title={t("manualManagement.misc.share")}>
      <div className="grid grid-cols-2 gap-2">
        <Button variant="outline" size="sm" onPress={() => copy("view", viewLink)} className="dark:border-border gap-1.5">
          <Link2 aria-hidden="true" className="size-3.5" />
          {copied === "view" ? t("manualManagement.list.copied") : t("manualManagement.misc.viewLink")}
        </Button>
        {canEdit ? (
          <Button variant="outline" size="sm" onPress={() => copy("edit", editLink)} className="dark:border-border gap-1.5">
            <Link2 aria-hidden="true" className="size-3.5" />
            {copied === "edit" ? t("manualManagement.list.copied") : t("manualManagement.misc.editLink")}
          </Button>
        ) : (
          <ComingSoon icon={Link2} label={t("manualManagement.misc.editLink")} variant="outline" />
        )}
        {print}
        <Button variant="outline" size="sm" onPress={() => setShowQr((v) => !v)} aria-expanded={showQr} className="dark:border-border gap-1.5">
          <QrCode aria-hidden="true" className="size-3.5" />
          {t("manualManagement.misc.qrCode")}
        </Button>
      </div>
      {showQr ? (
        <div className="mt-2 flex items-start gap-3">
          <div role="img" aria-label={svg ? "QR code of the view link" : t("manualManagement.misc.generatingQrCode")} className="border-border bg-card size-36 shrink-0 rounded-md border p-1.5 [&_svg]:size-full" dangerouslySetInnerHTML={{ __html: svg ?? "" }} />
          <p className="text-muted-foreground min-w-0 text-xs break-all">{viewLink}</p>
        </div>
      ) : null}
    </Section>
  );
}

function ComingSoon({
  icon: Icon,
  label,
  variant = "default",
  size = "sm",
  className,
}: {
  icon: LucideIcon;
  label: string;
  variant?: "default" | "outline";
  size?: "sm" | "lg";
  className?: string;
}) {
  const { t } = useTranslation();
  return (
    <TooltipTrigger delay={300} excludeFromTabOrder>
      <span className={cn("inline-flex", className)}>
        {/* `dark:border-border` on the outline variant: the Button's outline sets `dark:border-input`, and `--input` ==
            `--card` in dark → the border vanishes on the drawer surface (the documented gotcha). A dark-scoped override
            (base `border-border` alone loses to the variant's `dark:border-input`) restores a visible hairline. */}
        <Button
          variant={variant}
          size={size}
          isDisabled
          aria-label={t("manualManagement.details2.comingSoonLabel", { label })}
          className={cn("w-full", variant === "outline" && "dark:border-border")}
        >
          <Icon aria-hidden="true" />
          {label}
        </Button>
      </span>
      <Tooltip>{t("manualManagement.misc.comingSoon")}</Tooltip>
    </TooltipTrigger>
  );
}

/** The Edit action, permission-gated (ADR-002 Phase A) — mirrors the legacy `canUserSeeManual` EDIT rule computed by the
 *  drawer. Enabled only for an edit-entitled caller once `onEdit` is wired (the authoring surface, Phase 10); until then an
 *  entitled caller sees a "Coming soon" tooltip and a caller without edit rights sees an honest reason. The disabled
 *  reason uses `delay={0}` so the "why" appears immediately (a11y — never a silently-dead control). */
function EditButton({
  canEdit,
  disabledReason,
  onEdit,
}: {
  canEdit: boolean;
  disabledReason: string;
  onEdit?: () => void;
}) {
  const { t } = useTranslation();
  const enabled = canEdit && onEdit != null;
  const tooltip = enabled ? t("manualManagement.misc.editThisManual") : canEdit ? t("manualManagement.misc.comingSoon") : disabledReason;
  return (
    <TooltipTrigger delay={enabled ? 300 : 0} excludeFromTabOrder>
      <span className="flex min-w-0">
        {/* `dark:border-border`: the outline variant sets `dark:border-input`, and `--input` == `--card` in dark → the
            border vanishes on the drawer surface (documented gotcha). The dark override restores a visible hairline. */}
        <Button
          variant="outline"
          size="lg"
          isDisabled={!enabled}
          onPress={onEdit}
          aria-label={enabled ? t("manualManagement.misc.edit") : t("manualManagement.details2.editWithReason", { reason: tooltip })}
          className="dark:border-border w-full"
        >
          <Pencil aria-hidden="true" />
          {t("manualManagement.misc.edit")}
        </Button>
      </span>
      <Tooltip>{tooltip}</Tooltip>
    </TooltipTrigger>
  );
}
